import { KeyValue } from '@angular/common';
import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FullConditionsOfApplicabilityFragment } from '@lib/models/fragment/fiche-config';
import { ConseilConfig, CreateFicheConseilInput } from '@lib/models/generated/graphql';
import { AuthService } from '@lib/services/auth-service.service';
import { DocumentsService } from '@lib/services/documents.service';
import { gql } from 'apollo-angular';
import { QueryManagerService } from 'finzzle-angular-utils';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';

const path = 'assets/dummy.pdf'; //FIXME: temporary path for POC only

const gammeOrder = [
  'Epargne financière',
  'Epargne retraite',
  'Epargne Immobilière',
  'Solutions fiscales',
  'Diversification',
];
const leftColumnOrder = ['Epargne financière', 'Epargne retraite', 'Epargne Immobilière'];
const rightColumnOrder = ['Solutions fiscales', 'Diversification'];

const categorieNatureOrder = ['Versement', 'Arbitrage', 'Rachat', 'Autre'];

export const CONSEILCONFIG_TO_SELECT = gql`
  query activeConseilConfigs {
    activeConseilConfigs {
      id
      libelle
      conditionsOfApplicability {
        ...FullConditionsOfApplicability
      }
      configFormulaire {
        enveloppeConfig {
          lastChangeDate
        }
      }
      legacyDocumentsDetails {
        id
        fileNameWithExtension
        fileName
        denomination
        # fileConnectionInfo {   // fileConnectionInfo is not automatically fetched : we need to use the getStorageFile query
        #   sasUrl               // to avoid to fetch all the fileConnectionInfo for all the documents and use a out of date url
        # }                      // but also the API implementation does not handle fileConnectionInfo for multiple documents in the same conseilConfig: it leads to error (maybe because it's an exension in an extension)
      }
    }
  }
  ${FullConditionsOfApplicabilityFragment}
`;

type ConseilConfigByEnveloppe = {
  [gamme: string]: {
    [type: string]: {
      [enveloppe: string]: ConseilConfig[];
    };
  };
};

type ConseilConfigByCategorieNature = {
  [categorie: string]: ConseilConfig[];
};

@Component({
  selector: 'app-select-conseil-config-dialog',
  templateUrl: './select-conseil-config-dialog.component.html',
  styleUrls: ['./select-conseil-config-dialog.component.scss'],
})
export class SelectConseilConfigDialogComponent {
  leftColumnGroupedConfigs: ConseilConfigByEnveloppe;
  rightColumnGroupedConfigs: ConseilConfigByEnveloppe;
  selectedType: { [enveloppe: string]: ConseilConfig[] } | undefined = undefined; // configs from a type2, grouped by enveloppe
  backLabel: string | undefined = undefined;
  gammeLabel: string;
  configsByCategory: ConseilConfigByCategorieNature | undefined; // configs grouped by natureCategory
  isLoading: boolean;
  readonly categorieNatureOrder = categorieNatureOrder; //use for the order of the NatureCategorie in orderBy pipe
  readonly leftColumnOrder = leftColumnOrder; //use for the order of the gamme in orderBy pipe
  readonly rightColumnOrder = rightColumnOrder;
  readonly defaultTitle: string = "Sélectionnez l'enveloppe";
  readonly lastStepTitle: string = "Sélectionnez l'opération";
  isCreatingId: string | undefined;
  title: string = this.defaultTitle;
  isScrollable: boolean = false;
  constructor(
    private queryManager: QueryManagerService,
    private documentService: DocumentsService,
    private dialog: MatDialogRef<SelectConseilConfigDialogComponent>,
    private ficheConseilService: FicheConseilService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;

    let UserValidHabilitationsIds = this.authService.getUserValidHabilitations().map(hab => hab.habilitationId);

    this.queryManager
      .query<{ activeConseilConfigs: ConseilConfig[] }>({
        query: CONSEILCONFIG_TO_SELECT,
      })
      .subscribe(result => {
        const conseilConfigs = result?.data?.activeConseilConfigs;
        if (conseilConfigs) {
          // Group the conseil configs by gamme and type2 and enveloppe
          [this.leftColumnGroupedConfigs, this.rightColumnGroupedConfigs] =
            this.groupConseilConfigsByColumn(conseilConfigs);
          this.isLoading = false;
        }
      });
  }


  groupConseilConfigsByNatureCategorie(conseilConfigs: ConseilConfig[]): ConseilConfigByCategorieNature {
    return conseilConfigs.reduce((acc, config) => {
      const hasFormulaire = !!config.configFormulaire;
      const hasDocuments = (config.legacyDocumentsDetails?.length ?? 0) > 0;

      // we hide the conseil configs that have no formulaire and no documents
      if (!hasFormulaire && !hasDocuments) {
        return acc;
      }

      const natureOperation = config.conditionsOfApplicability?.natureOperation;
      if (!natureOperation) {
        return acc;
      }
      const categorieNavigation = natureOperation.categorieNavigation;
      if (!acc[categorieNavigation.libelle]) {
        acc[categorieNavigation.libelle] = [];
      }
      acc[categorieNavigation.libelle].push(config);

      return acc;
    }, {} as ConseilConfigByCategorieNature);
  }

  groupConseilConfigsByColumn(conseilConfigs: ConseilConfig[]): ConseilConfigByEnveloppe[] {
    const groupedConfigs = conseilConfigs.reduce((groupedConfigs, config) => {
      const enveloppeLibelle = config.conditionsOfApplicability.enveloppe?.libelle || '';
      const type2Libelle = config.conditionsOfApplicability.enveloppe?.type2Navigation.libelle;
      const gammeLibelle = config.conditionsOfApplicability.enveloppe?.type2Navigation.gammeNavigation.libelle;

      const hasFormulaire = !!config.configFormulaire;
      const hasDocuments = (config.legacyDocumentsDetails?.length ?? 0) > 0;

      // we hide the conseil configs that have no formulaire and no documents
      if (!hasFormulaire && !hasDocuments) {
        return groupedConfigs;
      }

      if (gammeLibelle && !groupedConfigs[gammeLibelle]) {
        groupedConfigs[gammeLibelle] = {};
      }
      if (gammeLibelle && type2Libelle && !groupedConfigs[gammeLibelle][type2Libelle]) {
        groupedConfigs[gammeLibelle][type2Libelle] = {};
      }
      if (
        gammeLibelle &&
        type2Libelle &&
        enveloppeLibelle &&
        !groupedConfigs[gammeLibelle][type2Libelle][enveloppeLibelle]
      ) {
        groupedConfigs[gammeLibelle][type2Libelle][enveloppeLibelle] = [];
      }

      if (gammeLibelle && type2Libelle && enveloppeLibelle) {
        groupedConfigs[gammeLibelle][type2Libelle][enveloppeLibelle].push(config);
      }

      return groupedConfigs;
    }, {} as ConseilConfigByEnveloppe);

    return this.splitGroupedConfigsOnColumns(groupedConfigs);
  }
  splitGroupedConfigsOnColumns(groupedConfigs: ConseilConfigByEnveloppe): ConseilConfigByEnveloppe[] {
    const leftColumn: ConseilConfigByEnveloppe = {};
    const rightColumn: ConseilConfigByEnveloppe = {};

    Object.keys(groupedConfigs).forEach(gamme => {
      if (leftColumnOrder.includes(gamme)) {
        leftColumn[gamme] = groupedConfigs[gamme];
      } else {
        rightColumn[gamme] = groupedConfigs[gamme];
      }
    });
    return [leftColumn, rightColumn];
  }
  onTypeClick(type2: any) {
    // because we use a pipe that is generic, we check the type of the object
    if (this.isKeyValueType2Type(type2)) {
      this.gammeLabel = type2.key;
      this.selectedType = type2.value;
      this.backLabel = this.gammeLabel;
      this.isScrollable = true;

      if (!this.isTypeWithMultipleEnveloppes(this.selectedType)) {
        this.configsByCategory = this.groupConseilConfigsByNatureCategorie(
          this.selectedType[Object.keys(this.selectedType)[0]]
        );
        this.title = this.lastStepTitle;
      }
    }
  }

  onEnveloppeClick(selectedEnveloppe: KeyValue<string, Array<ConseilConfig>>) {
    this.backLabel = `${this.gammeLabel} - ${selectedEnveloppe.key}`;
    this.title = this.lastStepTitle;
    this.configsByCategory = this.groupConseilConfigsByNatureCategorie(selectedEnveloppe.value);
  }

  onBackClick(): void {
    // back to enveloppe selection
    if (this.configsByCategory && this.selectedType && this.isTypeWithMultipleEnveloppes(this.selectedType)) {
      this.configsByCategory = undefined;
      this.backLabel = this.gammeLabel;
      this.title = this.defaultTitle;
      this.isScrollable = true;
      return;
    }
    // back to type2 selection (first step)
    if (this.selectedType) {
      this.title = this.defaultTitle;
      this.configsByCategory = undefined;
      this.selectedType = undefined;
      this.backLabel = undefined;
      this.isScrollable = false;
    }
  }

  isTypeWithMultipleEnveloppes(groupedType2: { [enveloppe: string]: ConseilConfig[] }): boolean {
    return Object.keys(groupedType2).length > 1;
  }
  onCreateFicheClick(config: ConseilConfig) {
    if (
      config &&
      config.conditionsOfApplicability.natureOperation &&
      config.conditionsOfApplicability.enveloppe &&
      config?.configFormulaire?.enveloppeConfig
    ) {
      const input: CreateFicheConseilInput = {
        conseilConfigId: config.id,
        enveloppe: {
          code: config.conditionsOfApplicability.enveloppe.code,
          libelle:
            config.conditionsOfApplicability.enveloppe.libelle ?? config.conditionsOfApplicability.enveloppe.code,
        },
        natureOperation: {
          id: config?.conditionsOfApplicability?.natureOperation?.id,
          libelle: config?.conditionsOfApplicability?.natureOperation?.libelle,
        },
        investisseurType: config.conditionsOfApplicability.typeInvestisseur,
      };
      this.isCreatingId = config.id;
      this.ficheConseilService.createNewFiche(input).then(createdFicheId => {
        this.isCreatingId = undefined;
        if (createdFicheId) {
          this.dialog.close(createdFicheId);
        }
      });
    }
  }
  onDownloadClick(config: ConseilConfig) {
    let downloadTasks = config.legacyDocumentsDetails?.map(async doc => {
      const docWithConnection = await this.documentService.getStorageFile(doc.id);
      await this.documentService.downloadDocumentFromUrl(
        docWithConnection?.fileConnectionInfo?.sasUrl ?? '',
        doc.fileName
      );
    });
    if (downloadTasks) {
      Promise.all(downloadTasks).then(() => {});
    }
  }

  private isKeyValueType2Type(type: any): type is KeyValue<string, { [enveloppe: string]: ConseilConfig[] }> {
    return (
      typeof type === 'object' &&
      type !== null &&
      'key' in type &&
      'value' in type &&
      typeof type.key === 'string' &&
      typeof type.value === 'object'
    );
  }

  hasAtLeastOneGamme(groupedConfig: ConseilConfigByEnveloppe): boolean {
    return Object.keys(groupedConfig).length > 0;
  }
}
